import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../primitives/Icon';
import { Platform, Pressable } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { type Input as TamaguiInput, XStack, YStack } from 'tamagui';
import { Input } from '../primitives/Input';
import type { RNTextInputCompat, RNTextInputCompatKeys } from '../primitives/rnTextInputCompat';
import { Text } from '../primitives/Text';
import { useThemeColors } from '../hooks/useThemeColors';
import type { TextInput } from 'react-native';
import {
  backspaceAtCursor,
  digitsToValue,
  formatDigits,
  inferEdit,
  typeDigitAtCursor,
  valueToDigits,
} from './TimeInput.helpers';

const isWeb = Platform.OS === 'web';

/** Value is always "HH:mm" (24h) or empty. */
export type TimeInputProps = Omit<
  React.ComponentProps<typeof TamaguiInput>,
  'value' | 'onChangeText' | RNTextInputCompatKeys
> &
  Omit<RNTextInputCompat, 'onChangeText'> & {
  /** Current time "HH:mm" (e.g. "09:00"). Empty string for unset. */
  value: string;
  /** Called with a clamped "HH:mm". */
  onChangeText: (value: string) => void;
  /** Show a small clock icon next to the input (decorative only).
   *  Default false — most callers render this inside a FieldRow that
   *  already has its own contextual icon ("schedule"). Opt in only for
   *  standalone uses where there's no parent icon. */
  showTimeIcon?: boolean | undefined;
  /** Show a "Use HH:mm" caption above the input on invalid values.
   *  Default false — validation messaging is the form's job, not the
   *  field's. Opt in only when the input renders without surrounding
   *  form context. */
  showFormatHint?: boolean | undefined;
  /** Show a clock picker button that opens the OS time wheel on
   *  iOS / Android. Default true on native, no-op on web (web has the
   *  typed mask). Mirrors DateInput's `showCalendar`. */
  showTimePicker?: boolean | undefined;
  /** Underlying Input primitive variant. Leave undefined to let the
   *  Input primitive pick: 'inline' when inside a RowFocusContext
   *  (FormRow, FieldRow, DateTimeInput, TimeRangeInput), 'default'
   *  when standalone. */
  variant?: 'default' | 'inline' | undefined;
  /**
   * Native picker display format (ADR-0076). `'24h'` (default) shows
   * the OS time wheel in 24-hour mode; `'12h'` flips it to AM/PM. The
   * typed mask + emitted value stay 24h `HH:mm` regardless — only the
   * picker UI changes.
   */
  format?: '12h' | '24h' | undefined;
  /**
   * IANA time zone the time is anchored to (ADR-0076). Currently used
   * to enrich the accessibility hint ("Enter time in Asia/Tokyo") —
   * doesn't change the emitted value. The parent converts via
   * `wallTimeToUtcMs` when an absolute instant is needed.
   */
  timeZone?: string | undefined;
};

/**
 * Positional digit-mask time input.
 *
 * - Displays `00:00` as the placeholder when there's no value yet.
 * - Once focused, the input behaves as a fixed 4-position mask: typing
 *   replaces the digit at the cursor and advances over the colon;
 *   backspace zero-fills the previous digit without shrinking the value.
 * - Hours clamp to 0–23, minutes 0–59.
 *
 * Implementation: positional logic is inferred from the controlled-input
 * diff between renders, not from key events. iOS Safari soft keyboards
 * don't fire `keydown` for character input, so the previous keydown-based
 * approach silently fell through to the browser's default behaviour on
 * mobile web. Diffing the text in `onChangeText` works on every platform.
 */
export const TimeInput = forwardRef<TextInput | null, TimeInputProps>(
  (
    {
      value,
      onChangeText,
      placeholder = '00:00',
      showTimeIcon = false,
      showFormatHint = false,
      showTimePicker = true,
      variant,
      format = '24h',
      timeZone,
      onFocus: callerFocus,
      onBlur: callerBlur,
      onKeyPress: callerKeyPress,
      ...rest
    },
    ref,
  ) => {
    const theme = useThemeColors();
    const inputRef = useRef<TextInput | null>(null);
    const [focused, setFocused] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    // Esc-to-revert: snapshot the committed value on focus so Esc can
    // restore it without losing what the user had before they started
    // editing. Per ADR-0076.
    const revertSnapshot = useRef<string | null>(null);

    const valueDigits = valueToDigits(value);

    // While focused, the display tracks what the user has literally typed
    // (positional digit buffer) — independent of the parent's clamped value.
    // Without this split, typing "9" in the hour slot got clamped to "23:00"
    // before the user could type the second digit, making the input feel
    // broken. On blur we resync to the clamped value so the displayed text
    // matches what was actually persisted.
    const [draftDigits, setDraftDigits] = useState<string>(() => valueToDigits(value));

    useEffect(() => {
      if (!focused) setDraftDigits(valueToDigits(value));
    }, [value, focused]);

    const display = (() => {
      if (focused) return formatDigits(draftDigits.padEnd(4, '0'));
      if (valueDigits.length === 4) return formatDigits(valueDigits);
      return ''; // empty → placeholder shows
    })();

    // ---------- DOM sync ----------
    //
    // After every keystroke we re-assert the canonical display text and
    // caret position directly on the underlying input. We can't rely on
    // React's controlled-value flow alone:
    //
    //   1. When our positional logic produces the SAME value (e.g., typing
    //      '0' over an existing '0'), React skips the re-render and the
    //      DOM keeps whatever stray text the user just typed.
    //   2. Even when the value changes, browsers reset the caret to the
    //      end of the new value — we want it where positional logic put it.
    //
    // RN's `setNativeProps({ text, selection })` works on both native and
    // react-native-web, so we use that uniformly.
    const syncDom = useCallback((text: string, position: number) => {
      requestAnimationFrame(() => {
        const node = inputRef.current as unknown as {
          setNativeProps?: ((props: {
            text?: string | undefined;
            selection?: { start: number; end: number } | undefined;
          }) => void) | undefined;
        } | null;
        if (!node) return;
        if (typeof node.setNativeProps === 'function') {
          try {
            node.setNativeProps({
              text,
              selection: { start: position, end: position },
            });
            return;
          } catch {
            // Fall through to DOM API.
          }
        }
        // Web fallback — direct DOM access if RN-web's setNativeProps
        // wasn't available for some reason.
        const dom = node as unknown as HTMLInputElement;
        if (typeof dom.setSelectionRange === 'function') {
          try {
            if (dom.value !== text) dom.value = text;
            dom.setSelectionRange(position, position);
          } catch {
            // Input was unmounted between schedule and callback.
          }
        }
      });
    }, []);

    // ---------- Unified onChangeText handler ----------
    const handleChangeText = useCallback(
      (newText: string) => {
        const oldText = display;
        const edit = inferEdit(oldText, newText);
        const oldDigits = draftDigits.padEnd(4, '0').slice(0, 4) || '0000';

        if (edit.kind === 'noop') {
          syncDom(display, display.length);
          return;
        }

        if (edit.kind === 'clear') {
          setDraftDigits('');
          if (value !== '') onChangeText('');
          return;
        }

        // Prefer the input's actual caret position over a left-to-right diff
        // scan. The diff scan is ambiguous when the edited character equals
        // its neighbors (e.g. backspacing the trailing '0' of "12:30" looks
        // identical to backspacing past the end), which causes consecutive
        // backspresses to get stuck on the same digit. selectionStart reads
        // the post-edit caret, which sits at the edit site.
        const dom = inputRef.current as unknown as {
          selectionStart?: number | null | undefined;
        } | null;
        const postEditCursor =
          dom && typeof dom.selectionStart === 'number'
            ? dom.selectionStart
            : null;

        let result: { digits: string; cursor: number } | null;
        if (edit.kind === 'delete') {
          let cursor: number;
          if (postEditCursor !== null) {
            cursor = postEditCursor + 1;
          } else {
            let i = 0;
            while (
              i < newText.length &&
              i < oldText.length &&
              oldText[i] === newText[i]
            )
              i++;
            cursor = i + 1;
          }
          result = backspaceAtCursor(oldDigits, cursor);
        } else {
          let cursor: number;
          if (postEditCursor !== null) {
            cursor = Math.max(0, postEditCursor - 1);
          } else {
            let i = 0;
            while (i < oldText.length && oldText[i] === newText[i]) i++;
            cursor = i;
          }
          result = typeDigitAtCursor(oldDigits, cursor, edit.char);
        }

        if (!result) {
          syncDom(display, display.length);
          return;
        }

        setDraftDigits(result.digits);
        const nextValue = digitsToValue(result.digits);
        if (nextValue !== value) onChangeText(nextValue);
        syncDom(formatDigits(result.digits), result.cursor);
      },
      [display, draftDigits, value, onChangeText, syncDom],
    );

    const onFocus = useCallback((e: Parameters<NonNullable<typeof callerFocus>>[0]) => {
      setFocused(true);
      // Stash the current committed value so Esc can restore it later.
      revertSnapshot.current = value;
      // Seed the positional mask with the current value's digits, or "0000"
      // if empty. The buffer stays internal — we don't auto-emit "00:00" to
      // the parent on focus, so just opening the input doesn't commit a value.
      const seed = valueToDigits(value);
      setDraftDigits(seed.length === 4 ? seed : '0000');
      callerFocus?.(e);
    }, [value, callerFocus]);

    const onBlur = useCallback((e: Parameters<NonNullable<typeof callerBlur>>[0]) => {
      setFocused(false);
      revertSnapshot.current = null;
      callerBlur?.(e);
    }, [callerBlur]);

    // Esc-to-revert (web only — native dismissal handles itself).
    const onKeyPress = useCallback(
      (e: Parameters<NonNullable<typeof callerKeyPress>>[0]) => {
        // RN-web normalises onKeyPress; native KeyboardEvent on web uses
        // `key`. Either way, we look for 'Escape'.
        const key = (e?.nativeEvent as { key?: string } | undefined)?.key ?? '';
        if (key === 'Escape' && revertSnapshot.current !== null) {
          const snapshot = revertSnapshot.current;
          const seed = valueToDigits(snapshot);
          setDraftDigits(seed.length === 4 ? seed : '');
          if (snapshot !== value) onChangeText(snapshot);
          syncDom(formatDigits(seed.padEnd(4, '0')), 0);
        }
        callerKeyPress?.(e);
      },
      [callerKeyPress, onChangeText, value, syncDom],
    );

    // Native wheel picker: tap the clock icon → opens the OS time picker.
    // Web keeps the typed mask (no native picker available there).
    const showNativeTimePickerButton = showTimePicker && !isWeb;

    const valueToDate = (v: string): Date => {
      const d = new Date();
      if (/^\d{2}:\d{2}$/.test(v)) {
        const [h, m] = v.split(':').map(Number);
        d.setHours(h!, m, 0, 0);
      } else {
        d.setHours(0, 0, 0, 0);
      }
      return d;
    };

    const handlePickerChange = (_: unknown, d: Date | undefined) => {
      if (Platform.OS === 'android') setPickerOpen(false);
      if (d) {
        const h = String(d.getHours()).padStart(2, '0');
        const m = String(d.getMinutes()).padStart(2, '0');
        onChangeText(`${h}:${m}`);
      }
    };

    // Outline suppression + focusStyle now live on the Input primitive, so
    // we only need to size the field here.
    const inputStyle = {
      flex: 1,
      minHeight: 40,
    } as const;

    return (
      <YStack gap="$1" width="100%" minWidth={0}>
        {showFormatHint && display && !/^\d{2}:\d{2}$/.test(display) ? <Text variant="caption" color="$textError" marginLeft={2}>
            Use HH:mm (e.g. 09:00)
          </Text> : null}
        <XStack alignItems="center" gap="$2">
          <Input
            ref={(r) => {
              (inputRef).current = r;
              if (typeof ref === 'function') ref(r);
              else if (ref) (ref).current = r;
            }}
            variant={variant}
            width="100%"
            value={display}
            onChangeText={handleChangeText}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            keyboardType="number-pad"
            inputMode="numeric"
            accessibilityLabel="Time"
            accessibilityHint={
              timeZone
                ? `Enter time in 24-hour format, e.g. 09:00 (${timeZone})`
                : 'Enter time in 24-hour format, e.g. 09:00'
            }
            {...(onKeyPress !== undefined ? { onKeyPress } : null)}
            // Allow one extra char so the diff handler can observe a single-
            // char insertion past the canonical 5-char display before we
            // snap it back via the controlled value.
            maxLength={6}
            style={inputStyle}
            editable
            {...(rest as Record<string, unknown>)}
          />
          {showTimeIcon && !showNativeTimePickerButton ? <XStack
              padding={4}
              pointerEvents="none"
              accessible={false}
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <Icon
                name="schedule"
                size={20}
                color={theme.textSubdued}
              />
            </XStack> : null}
          {showNativeTimePickerButton ? <>
              <Pressable
                onPress={() => setPickerOpen(true)}
                accessibilityLabel="Open time picker"
                hitSlop={8}
                style={({ pressed }) => [
                  { padding: 4, opacity: pressed ? 0.6 : 1 },
                ]}
              >
                <Icon
                  name="schedule"
                  size={20}
                  color={theme.textSubdued}
                />
              </Pressable>
              {pickerOpen ? <DateTimePicker
                  value={valueToDate(value)}
                  mode="time"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  is24Hour={format === '24h'}
                  {...(timeZone ? { timeZoneName: timeZone } : null)}
                  onChange={handlePickerChange}
                /> : null}
            </> : null}
        </XStack>
      </YStack>
    );
  },
);

TimeInput.displayName = 'TimeInput';
